import {
  useRef,
  useState,
} from 'react';
import {
  useStripe,
  useElements,
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
} from '@stripe/react-stripe-js';
import * as Yup from 'yup';
import {
  useDispatch,
  useSelector,
} from 'react-redux';
import {
  Formik,
  Field,
} from 'formik';
import {
  toast,
} from 'react-toastify';

import {
  coreUtils,
  coreActions,
  coreSelectors,
} from '@gymcloud/core';

import {
  TextField,
  SelectField,
  FormElementField,
} from 'web-components';
import {
  Flex,
} from 'web-styled';
import {
  getRoundedAmount,
  trackAnalyticsEvent,
} from 'web-utils';

import {
  caStatesOptions,
  usStatesOptions,
  countriesOptions,
} from './data';
import * as S from '../styled';

const countryZipRequiredList = ['us', 'ca'];
const zipRegex = {
  us: /^([0-9]{5})(?:[-\s]*([0-9]{4}))?$/,
  ca: /^([A-Z][0-9][A-Z])\s*([0-9][A-Z][0-9])$/,
};

const requiredReplacement = {
  cardExpiryIsValid: 'Expiration date is required',
  CVCIsValid: 'CVC is required',
  state: 'State is required',
  zip: 'Zip is required',
};

const validationSchema = () => Yup.lazy(
  values => Yup.object().shape({
    cardNumberIsValid: Yup.mixed()
      .oneOf([true, null], 'Please enter a valid card number')
      .required('Credit card is required'),
    cardExpiryIsValid: Yup.mixed()
      .oneOf([true, null], 'Please enter a valid card expiration date')
      .required('Required'),
    CVCIsValid: Yup.mixed()
      .oneOf([true, null], 'Please enter a valid CVC')
      .required('Required'),
    cardHolderName: Yup.string()
      .required('Name is required'),
    ...(countryZipRequiredList.indexOf(values.country) !== -1
      ? {
        state: Yup.string()
          .required('Required'),
        zip: Yup.string()
          .matches(
            zipRegex[values.country],
            {
              message: 'Please enter a valid zip code.',
              excludeEmptyString: true,
            },
          )
          .required('Required'),
      }
      : {}
    ),
  }),
);

const mapState = state => {
  const plans = coreSelectors.getResourceMappedList(
    'productPlans',
  )(state);

  return {
    activePlans: plans
      .filter(plan => plan.active)
      .map(plan => ({
        value: plan.id,
        label: `${coreUtils.getIntervalDescription(
          plan.interval,
        )}, $${getRoundedAmount(plan.amount)}/${coreUtils.getIntervalABBR(
          plan.interval,
        )}`,
      })),
  };
};

type Props = {
  id: number,
  isPlan: boolean,
  secondButtonText: string,
  userData: any,
  onSuccess: { (skipped: boolean): void },
  onPlanChange: { (plan: any): void },
};

const StepTwoForm = ({
  id,
  isPlan,
  secondButtonText,
  userData,
  onPlanChange,
  onSuccess,
}: Props) => {
  const stripe = useStripe();
  const elements = useElements();
  const [savedClient, setSavedClient] = useState(null);
  const [savedPM, setSavedPM] = useState(null);
  const isSubmittingRef = useRef(false);
  const dispatch = useDispatch();
  const { activePlans } = useSelector(mapState);

  function createClient(data: any = {}) {
    return new Promise((resolve, reject) => {
      if (savedClient) {
        resolve(savedClient);
      } else {
        dispatch(
          coreActions.requestCreateUser({
            data: {
              user: {
                ...userData,
                signup_source: 'desktop',
              },
              profile: {
                first_name: userData.first_name,
                last_name: userData.last_name,
                ...data,
              },
            },
            callbacks: {
              onSuccess: (res: any) => {
                setSavedClient(res);
                resolve(res);
              },
              onError: reject,
            },
          }),
        );
      }
    });
  }

  function createPaymentMethod({
    name,
    country,
    state,
    zip,
  }) {
    return new Promise((resolve, reject) => {
      if (savedPM) {
        resolve(savedPM);
      } else {
        stripe
          .createPaymentMethod({
            type: 'card',
            card: elements.getElement('cardNumber'),
            billing_details: {
              name,
              address: countryZipRequiredList.indexOf(country) !== -1
                ? {
                  state,
                  country,
                  postal_code: zip,
                }
                : {
                  country,
                },
            },
          })
          .then((result: any) => {
            if (result.error) {
              reject(result.error);
            } else {
              dispatch(
                coreActions.requestCreatePaymentMethod({
                  body: {
                    id: result.paymentMethod.id,
                  },
                  callbacks: {
                    onSuccess: (res: any) => {
                      setSavedPM(res);
                      resolve(res);
                    },
                    onError: reject,
                  },
                }),
              );
            }
          });
      }
    });
  }

  function buyProduct({ coupon, clientId }) {
    return new Promise((resolve, reject) => {
      dispatch(
        coreActions.requestBuyProduct({
          payload: coupon ? {
            coupon,
            client_id: clientId,
            product_id: id,
          } : {
            client_id: clientId,
            product_id: id,
          },
          callbacks: {
            onSuccess: resolve,
            onError: reject,
          },
        }),
      );
    });
  }

  function finishBuyProduct(paymentIntentId: number) {
    return new Promise((resolve, reject) => {
      dispatch(
        coreActions.requestFinishBuy({
          paymentIntentId,
          callbacks: {
            onSuccess: resolve,
            onError: reject,
          },
        }),
      );
    });
  }

  function setPlan({ coupon }: { coupon?: string }) {
    return new Promise((resolve, reject) => {
      dispatch(
        coreActions.requestSetPlan({
          isClient: true,
          body: coupon ? {
            coupon,
            product_plan_id: id,
          } : {
            product_plan_id: id,
          },
          callbacks: {
            onSuccess: resolve,
            onError: reject,
          },
        }),
      );
    });
  }

  const onSubmit = async ({
    cardHolderName,
    country,
    state,
    zip,
    coupon,
  }: any) => {
    if (isSubmittingRef.current) return false;

    isSubmittingRef.current = true;
    trackAnalyticsEvent('signup_clicked_contintue_account')

    try {
      if (stripe) {
        const client: any = await createClient({
          country,
          state,
          zip,
        });

        const paymentMethod: any = await createPaymentMethod({
          name: cardHolderName,
          zip,
          state,
          country,
        });

        if (isPlan) {
          await setPlan({ coupon });
          onSuccess(false);
        } else {
          const paymentIntent: any = await buyProduct({ coupon, clientId: client.id });
          const { error } = await stripe.confirmCardPayment(
            paymentIntent.client_secret,
            {
              payment_method: paymentMethod.id,
            },
          );

          if (error) {
            throw error;
          }

          await finishBuyProduct(paymentIntent.id);
          onSuccess(false);
        }
      } else {
        console.log("Stripe.js hasn't loaded yet.");
      }
    } catch (e) {
      console.log('catch', e);
    } finally {
      isSubmittingRef.current = false;
    }

    return true;
  };

  const handleSkip = async () => {
    trackAnalyticsEvent('signup_clicked_skipped_account')

    try {
      await createClient();
      onSuccess(true);
    } catch (e) {
      console.log('create client error');
    }
  };

  return (
    <Formik
      initialValues={{
        plan_id: id,
        cardNumberIsValid: null,
        cardExpiryIsValid: null,
        CVCIsValid: null,
        cardHolderName: '',
        country: 'us',
        zip: '',
        state: '',
        coupon: '',
      }}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {(formikProps) => {
        const location = {
          ca: {
            label: 'Province*',
            placeholder: 'Select your province',
            zipPlaceholder: 'A1A 1A1',
            options: caStatesOptions,
          },
          us: {
            label: 'State*',
            placeholder: 'Select your state',
            zipPlaceholder: '12345',
            options: usStatesOptions,
          },
        }[formikProps.values.country];

        return (
          <S.Form column onSubmit={formikProps.handleSubmit}>
            <Flex column width="100%" spacing={15}>
              {onPlanChange && (
                <Field
                  name="plan_id"
                  label="Subscription"
                  color="#121827"
                  placeholder="Select plan"
                  options={activePlans}
                  component={SelectField}
                  onChange={onPlanChange}
                />
              )}
              <Field
                type="text"
                name="cardNumberIsValid"
                label="Card Number*"
                color="#121827"
                Element={CardNumberElement}
                component={FormElementField}
              />
              <Flex
                spacing={15}
                width="100%"
                justify="space-between"
              >
                <Field
                  type="text"
                  name="cardExpiryIsValid"
                  label="Expiration Date*"
                  color="#121827"
                  width="100px"
                  className="card-expiry"
                  errorStyle={{
                    margin: 0,
                    alignSelf: 'flex-start',
                  }}
                  Element={CardExpiryElement}
                  component={FormElementField}
                />
                <Field
                  type="text"
                  name="CVCIsValid"
                  label="CVC*"
                  color="#121827"
                  width="80px"
                  className="card-cvc"
                  errorStyle={{
                    margin: 0,
                    textAlign: 'right',
                  }}
                  Element={CardCvcElement}
                  component={FormElementField}
                />
              </Flex>
              <Field
                type="text"
                name="cardHolderName"
                label="Cardholder Name*"
                color="#121827"
                component={TextField}
                placeholder="Enter Name"
                autoComplete="off"
              />
              <Field
                name="country"
                label="Country*"
                color="#121827"
                options={countriesOptions}
                component={SelectField}
                onChange={() => {
                  formikProps.setFieldValue('zip', '');
                  formikProps.setFieldValue('state', '');
                }}
                maxMenuHeight={500}
                placeholder="-- Select Country --"
                theme={{
                  borderRadius: 5,
                }}
                styles={{
                  option: provided => ({
                    ...provided,
                    fontSize: '12px',
                    lineHeight: '12px',
                    fontWeight: 500,
                  }),
                  valueContainer: provided => ({
                    ...provided,
                    color: '#495057',
                    fontSize: '1rem',
                    lineHeight: '1.5',
                    fontWeight: 500,
                  }),
                }}
              />
              {countryZipRequiredList.indexOf(formikProps.values.country) !== -1 && (
                <S.Location>
                  <Field
                    name="state"
                    label={location?.label}
                    color="#121827"
                    options={location?.options}
                    component={SelectField}
                    maxMenuHeight={500}
                    placeholder={location?.placeholder}
                    theme={{
                      borderRadius: 5,
                    }}
                    styles={{
                      option: provided => ({
                        ...provided,
                        fontSize: '12px',
                        lineHeight: '12px',
                        fontWeight: 500,
                      }),
                      valueContainer: provided => ({
                        ...provided,
                        color: '#495057',
                        fontSize: '1rem',
                        lineHeight: '1.5',
                        fontWeight: 500,
                      }),
                    }}
                  />
                  <Field
                    type="text"
                    name="zip"
                    label="Zip*"
                    color="#121827"
                    component={TextField}
                    placeholder={location?.zipPlaceholder}
                    autoComplete="off"
                  />
                </S.Location>
              )}
              <Field
                type="text"
                name="coupon"
                label="Coupon"
                color="#121827"
                component={TextField}
                placeholder="12345678"
                autoComplete="off"
              />
              <S.SubmitButton
                isLoading={formikProps.isSubmitting}
                isDisabled={formikProps.isSubmitting}
                onClick={() => {
                  formikProps.validateForm().then(errors => {
                    Object.keys(errors)
                      .reverse()
                      .forEach(key => {
                        const error = formikProps.errors[key] === 'Required'
                          ? requiredReplacement[key]
                          : formikProps.errors[key];
                        toast.error(error);
                      });
                  });
                }}
              >
                {secondButtonText}
              </S.SubmitButton>
              <S.SubmitLink onClick={handleSkip}>
                Skip the premium content for now and upgrade anytime
              </S.SubmitLink>
            </Flex>
          </S.Form>
        );
      }}
    </Formik>
  );
};

export default StepTwoForm;
